import { useCallback, useMemo } from 'react';
import { useGlobalContext } from '../../../contexts';

import { SizeGuide, Link } from '../../../snippets';

export function ProductOptionValuesLabel({
  enabledsizeGuide,
  name,
  groupingProductsMapByColor,
  groupValues,
  selectedOptions,
  productType,
  selectedValue,
  selectedColor,
  sizeGuides,
  tabsEnabled,
  tabsLinkEnabled,
  disableGroupHistory,
  needsSelection,
  optionChanges,
  isProductTypePresent,
  isFullWidthTabs,
  index,
  selectedTab,
  setSelectedTab,
  selected,
  isColor = false,
  groupedBreadcrumbs,
}) {
  const {
    actions: { openModal },
  } = useGlobalContext();

  const trimmedColor = selectedColor?.includes('| ') ? selectedColor?.split('| ')[1] : selectedColor;
  const valueToUse = groupValues?.some(value => value === trimmedColor)
  ? trimmedColor
  : groupValues?.find(value => value.endsWith(`| ${trimmedColor}`)) || groupValues?.[0];

  const newSelectedOptions = {
    ...selectedOptions,
    Color: valueToUse,
  };

  const selectedVariantFromOptions = useMemo(() => {
    if (!groupingProductsMapByColor || !newSelectedOptions) return null;

    const variants = groupingProductsMapByColor[newSelectedOptions.Color]?.variants;
    if (!variants) return null;

    let bestMatch = null;
    let maxMatches = -1;

    for (const variant of variants) {
        const { selectedOptionsMap } = variant;
        const matches = Object.keys(newSelectedOptions).reduce((count, key) => {
            return count + (newSelectedOptions[key] === selectedOptionsMap[key] ? 1 : 0);
        }, 0);

        if (matches >= maxMatches) {
            maxMatches = matches;
            bestMatch = variant;
        }
    }

    return bestMatch;
  }, [groupingProductsMapByColor, newSelectedOptions]);

  const numberOfProperties = selectedOptions ? Object.keys(selectedOptions).length : 0;
  const numberOfPropertiesChanged = optionChanges ? Object.keys(optionChanges).length : 0;

  const url = useMemo(() => {
    if (!selectedVariantFromOptions) return null;

    let params = '';
    if (typeof window !== 'undefined') {
      const { search } = window.location;
      params = new URLSearchParams(search);
      params.set('variant', selectedVariantFromOptions.legacyResourceId);
      if (!needsSelection) {
        params.set('s', needsSelection);
      } else {
        if (numberOfProperties > 2 && numberOfPropertiesChanged > 1) {
          Object.entries(optionChanges).forEach(([key, value]) => {
            if (key !== 'Color') {
              params.set(key, value.toString());
            }
          });
        }
      }
    }

    return `/products/${selectedVariantFromOptions.product.handle}?${params}`;
  }, [selectedVariantFromOptions, needsSelection, optionChanges, numberOfPropertiesChanged]);

  const handleSizeGuideClick = useCallback(() => {
    openModal(
      <SizeGuide 
        productType={productType}
        sizeGuides={sizeGuides}
        className="lg:h-[calc(100%-4rem)] lg:w-[64rem] lg:max-w-full lg:max-h-full"
        extraClassName="lg:max-h-full lg:h-full"
      />
    );
  }, [productType, sizeGuides]);

  const selectedClass =
    (selectedValue && selectedTab === null) ||
    (selectedTab !== null && index === selectedTab)
      ? 'border-primary bg-white'
      : '';
  
  return tabsEnabled && isProductTypePresent ? (
    <>
      {url && tabsLinkEnabled ? (
        <Link
          href={url}
          className={`btn-inverse-light btn-sm shadow-hairline text-label !font-normal ${selectedClass} ${isFullWidthTabs ? 'flex-grow' : 'lg:px-4'}`}
          id={`color-tabs-item-${index}`}
          data-hs-tab={`color-tabs-${index}`}
          aria-controls={`color-tabs-${index}`}
          role="tab"
          onClick={(e) => {
            if (selected) e.preventDefault();
            if (groupedBreadcrumbs?.title && groupedBreadcrumbs?.handle) {
              sessionStorage.setItem('savedCollectionTitle', groupedBreadcrumbs?.title);
              sessionStorage.setItem('savedCollectionHandle', groupedBreadcrumbs?.handle);
              sessionStorage.setItem('savedHistory', groupedBreadcrumbs?.path);
            }
          }}
          scroll={false}
          replace={disableGroupHistory ? true : false}
        >
          {name}
        </Link>
      ) : (
        <button
          type="button"
          className={`btn-inverse-light btn-sm shadow-hairline text-label !font-normal ${selectedClass} ${isFullWidthTabs ? 'flex-grow' : 'lg:px-4'}`}
          id={`color-tabs-item-${index}`}
          data-hs-tab={`color-tabs-${index}`}
          aria-controls={`color-tabs-${index}`}
          role="tab"
          onClick={() => setSelectedTab(index)}
        >
          {name}
        </button>
      )}
    </>
  ) : (
    <div className="mb-2 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-label text-darkGray">{name}</h3>
        {(selected && selectedValue || isColor && selectedValue) && (
          <p className="text-sm text-mediumGray">
            {selectedValue.split('|').length > 1
              ? selectedValue.split('|')[1].trim()
              : selectedValue}
          </p>
        )}
      </div>

      {name === 'Size' && enabledsizeGuide && (
        <button
          className="text-underline text-xs"
          onClick={handleSizeGuideClick}
          type="button"
        >
          Size Guide
        </button>
      )}
    </div>
  );
}

ProductOptionValuesLabel.displayName = 'ProductOptionValuesLabel';
